import React, { useCallback, useEffect, useState } from 'react';
import {
  getApprovalSummary,
  setApprovalRequired,
} from '../../../services/curriculumApprovalService';

/**
 * Per-tenant switch for the feat-270 curriculum approval gate
 * (feat-271; ADR-feat-270-api-contract §3.5).
 *
 * feat-270 shipped the gate, the queue and all five endpoints, but wired NO
 * control to `PUT .../approvals/required`. The only way to turn the feature on
 * was an authenticated API call by hand, which is why it looked missing to the
 * first admin who went looking for it. This card is that control.
 *
 * Deliberately modelled on `TenantLanguageDefaultCard` — same shell, same save
 * flow, same "render always, disable when you may not" posture — because this is
 * an interim ops surface. The whole tenant-admin screen is due a revision; this
 * should be cheap to delete when that happens, so it borrows rather than
 * invents.
 *
 * ── WHAT IT MAY NOT BORROW: THE RBAC GATE ──────────────────────────────────
 *
 * Its siblings are Admin-OR-Corporate. This endpoint is `require_admin`, and the
 * asymmetry is the point (ADR-B §3.5): a corporate manager who could turn the
 * gate OFF could clear their own pending changes by disabling the feature, which
 * defeats D6. So `canManage` here means ADMIN ONLY, and the parent must resolve
 * it FAIL-CLOSED.
 *
 * TenantDashboard carries two admin flags and picking the wrong one is a live
 * bug, not a style choice: `isAdmin` (:284) defaults to TRUE when `userContext`
 * cannot be resolved, and feat-196's G11 finding came from chaining exactly that
 * fail-OPEN flag to a write. `isAdminForLiveClient` (:303) is the fail-closed
 * form. This card is passed the fail-closed shape.
 *
 * ⚠ This is PRESENTATION, never the guard. The boundary is the backend's 403.
 */

/** What the card is currently able to say about the flag. */
type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; required: boolean }
  /**
   * The fetch failed, so the flag's value is UNKNOWN. Rendering an "Off" switch
   * here would be a claim we cannot support — and the wrong one is the dangerous
   * direction, since "Off" reads as "edits go straight to phones". Say so
   * instead, and leave the control disabled.
   */
  | { kind: 'error'; message: string };

/** Outcome of a flip, kept separate from `LoadState` so a stale result cannot
 *  survive a subsequent reload of the flag itself. */
type FlipResult =
  | { kind: 'none' }
  | { kind: 'ok'; text: string }
  | { kind: 'failed'; text: string };

const errText = (e: unknown): string =>
  e instanceof Error ? e.message : String(e);

const TenantCurriculumApprovalCard: React.FC<{
  tenantId: string;
  /** ADMIN only, resolved fail-closed by the parent. Not Admin-or-Corporate. */
  canManage: boolean;
}> = ({ tenantId, canManage }) => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FlipResult>({ kind: 'none' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const summary = await getApprovalSummary(tenantId);
      setState({ kind: 'ready', required: summary.approval_required });
    } catch (e) {
      setState({ kind: 'error', message: errText(e) });
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = state.kind === 'ready' ? state.required : null;
  const target = current === null ? null : !current;

  const flip = async () => {
    if (target === null) return;
    setSaving(true);
    setResult({ kind: 'none' });
    try {
      const res = await setApprovalRequired(tenantId, target);
      setState({ kind: 'ready', required: res.approval_required });
      setResult({
        kind: 'ok',
        text: res.approval_required
          ? // The counts come from the RESPONSE, never from the summary. With the
            // flag off the summary reports `lessons_total: 0` by design (it does
            // no derivation at all), so promising a number BEFORE the flip would
            // reliably promise zero.
            `Approval is on. ${res.backfilled_lessons} ${
              res.backfilled_lessons === 1 ? 'lesson' : 'lessons'
            } and ${res.backfilled_items} ${
              res.backfilled_items === 1 ? 'item' : 'items'
            } were approved as they are now, so nothing changed on phones.`
          : 'Approval is off. Edits now reach phones as soon as they are saved. Existing approvals were kept.',
      });
      setConfirming(false);
    } catch (e) {
      setResult({ kind: 'failed', text: `Could not change this: ${errText(e)}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="tenant-curriculum-approval-card"
    >
      <h2 className="text-lg font-semibold text-ink mb-1">Curriculum approval</h2>
      <p className="text-sm text-inkMuted mb-3">
        When this is on, edits to lessons, questions and rehearsals wait for
        someone to approve them before they reach employees&apos; phones. When it
        is off, every saved edit is live immediately.
      </p>

      {state.kind === 'loading' && (
        <p className="text-sm text-inkMuted" data-testid="tenant-approval-loading">
          Checking…
        </p>
      )}

      {state.kind === 'error' && (
        <div data-testid="tenant-approval-load-error">
          <p className="text-sm text-red-700">
            Could not check whether approval is on: {state.message}
          </p>
          <button
            type="button"
            className="mt-2 text-sm font-medium px-3 py-1 rounded bg-gray-200 text-ink hover:bg-gray-300"
            onClick={() => void load()}
            data-testid="tenant-approval-retry"
          >
            Try again
          </button>
        </div>
      )}

      {state.kind === 'ready' && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`text-sm font-semibold ${
                state.required ? 'text-ink' : 'text-inkMuted'
              }`}
              data-testid="tenant-approval-state"
            >
              {state.required
                ? 'On — edits need approval'
                : 'Off — edits go live immediately'}
            </span>
            {!confirming && (
              <button
                type="button"
                className="text-sm font-medium px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={!canManage || saving}
                onClick={() => {
                  setResult({ kind: 'none' });
                  setConfirming(true);
                }}
                data-testid="tenant-approval-toggle"
              >
                {state.required ? 'Turn off' : 'Turn on'}
              </button>
            )}
          </div>

          {/* An inline confirm, not `window.confirm`: a native dialog blocks the
              page and cannot carry the sentence that actually matters here. */}
          {confirming && (
            <div
              className="mt-3 rounded border border-gray-300 bg-cream p-3"
              data-testid="tenant-approval-confirm"
            >
              <p className="text-sm text-ink mb-2">
                {state.required
                  ? 'Turn approval off? Every saved edit will reach phones immediately, with no review. Work already approved stays approved.'
                  : 'Turn approval on? Everything in this curriculum will be approved exactly as it is right now, so nothing on phones changes today. From then on, each new edit waits for approval.'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-sm font-medium px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={!canManage || saving}
                  onClick={() => void flip()}
                  data-testid="tenant-approval-confirm-yes"
                >
                  {saving
                    ? 'Saving…'
                    : state.required
                      ? 'Yes, turn it off'
                      : 'Yes, turn it on'}
                </button>
                <button
                  type="button"
                  className="text-sm font-medium px-3 py-1 rounded bg-gray-200 text-ink hover:bg-gray-300 disabled:opacity-50"
                  disabled={saving}
                  onClick={() => setConfirming(false)}
                  data-testid="tenant-approval-confirm-no"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {result.kind !== 'none' && (
        <p
          className={`mt-2 text-sm ${
            result.kind === 'ok' ? 'text-inkMuted' : 'text-red-700'
          }`}
          data-testid="tenant-approval-result"
        >
          {result.text}
        </p>
      )}

      {!canManage && (
        <p className="mt-2 text-xs text-inkMuted" data-testid="tenant-approval-admin-note">
          Only Bespoke admins can turn curriculum approval on or off.
        </p>
      )}
    </div>
  );
};

export default TenantCurriculumApprovalCard;
